/* Created by Interactive 63 - https://interactive63.com */
/**
 * Keyframe copy and paste. A copy records each keyframe's element, property, value, easing and
 * its time relative to the earliest copied keyframe, and is kept in memory and written to the
 * system clipboard as JSON with a type marker so it also pastes into another tab.
 */
import type {
  Easing,
  ElementId,
  MotionHandles,
  Project,
  PropertyKey,
  PropertyValue,
} from '../core/model/types';
import { canAddProperty, setKeyframe } from '../core/timeline/keyframes';
import type { Store } from './store/store';
import { toast } from './ui/toast';

export interface ClipEntry {
  elementId: ElementId;
  property: PropertyKey;
  /** Milliseconds after the earliest copied keyframe. */
  offset: number;
  value: PropertyValue;
  easing: Easing;
  motion?: MotionHandles;
}

export interface ClipData {
  svgTempoKeyframes: 1;
  elements: ElementId[];
  entries: ClipEntry[];
}

export interface Placement {
  elementId: ElementId;
  property: PropertyKey;
  time: number;
  value: PropertyValue;
  easing: Easing;
  motion?: MotionHandles;
}

export interface PastePlan {
  placements: Placement[];
  /** Keyframes skipped because the property does not apply to the target. */
  skipped: number;
  /** Keyframes dropped because they land after the end of the scene. */
  dropped: number;
}

let memoryClip: ClipData | null = null;

/** Records the selected keyframes as clip data, or null when nothing is selected. */
export function buildClip(project: Project, keyframeIds: readonly string[]): ClipData | null {
  const wanted = new Set(keyframeIds);
  const found = project.tracks.flatMap((track) =>
    track.keyframes.filter((kf) => wanted.has(kf.id)).map((kf) => ({ track, kf })),
  );
  if (found.length === 0) return null;
  const earliest = Math.min(...found.map(({ kf }) => kf.time));
  return {
    svgTempoKeyframes: 1,
    elements: [...new Set(found.map(({ track }) => track.elementId))],
    entries: found.map(({ track, kf }) => ({
      elementId: track.elementId,
      property: track.property,
      offset: kf.time - earliest,
      value: kf.value,
      easing: kf.easing,
      ...(kf.motion ? { motion: kf.motion } : {}),
    })),
  };
}

/** Converts clip data to the JSON text written to the system clipboard. */
export function serialiseClip(clip: ClipData): string {
  return JSON.stringify(clip);
}

/** Reads clip data from clipboard text, or returns null when the text is not keyframe JSON. */
export function parseClip(text: string): ClipData | null {
  if (!text.includes('svgTempoKeyframes')) return null;
  try {
    const parsed = JSON.parse(text) as Partial<ClipData>;
    if (
      parsed.svgTempoKeyframes !== 1 ||
      !Array.isArray(parsed.entries) ||
      !Array.isArray(parsed.elements)
    ) {
      return null;
    }
    const valid = parsed.entries.every(
      (e) =>
        typeof e.elementId === 'string' &&
        typeof e.property === 'string' &&
        typeof e.offset === 'number' &&
        typeof e.easing === 'object',
    );
    return valid ? (parsed as ClipData) : null;
  } catch {
    return null;
  }
}

/**
 * Plans where pasted keyframes go. They land on the original elements, unless the clip came from
 * a single element and a different selection is active, in which case they go to the selected
 * elements. Keyframes whose property does not apply, or that fall past the end, are counted.
 */
export function planPaste(
  project: Project,
  clip: ClipData,
  selectedElementIds: readonly ElementId[],
  playhead: number,
): PastePlan {
  const sameSelection =
    selectedElementIds.length === clip.elements.length &&
    clip.elements.every((id) => selectedElementIds.includes(id));
  const retarget = selectedElementIds.length > 0 && !sameSelection && clip.elements.length === 1;
  const targets: (ElementId | undefined)[] = retarget ? [...selectedElementIds] : [undefined];

  const plan: PastePlan = { placements: [], skipped: 0, dropped: 0 };
  const start = Math.round(playhead);
  for (const target of targets) {
    for (const entry of clip.entries) {
      const elementId = target ?? entry.elementId;
      if (!canAddProperty(project, elementId, entry.property)) {
        plan.skipped += 1;
        continue;
      }
      const time = start + Math.round(entry.offset);
      if (time > project.scene.duration) {
        plan.dropped += 1;
        continue;
      }
      plan.placements.push({
        elementId,
        property: entry.property,
        time,
        value: entry.value,
        easing: entry.easing,
        ...(entry.motion ? { motion: entry.motion } : {}),
      });
    }
  }
  return plan;
}

/** Writes a plan into a project draft and returns the IDs of the keyframes it created. */
export function applyPlan(draft: Project, plan: PastePlan): string[] {
  return plan.placements.map((p) => {
    const keyframe = setKeyframe(draft, p.elementId, p.property, p.time, p.value, p.easing);
    if (p.motion) keyframe.motion = { ...p.motion };
    return keyframe.id;
  });
}

/** Reports skipped and dropped keyframes with a toast. */
function explain(plan: PastePlan): void {
  if (plan.skipped > 0) {
    toast.info(
      `Skipped ${plan.skipped} keyframe${plan.skipped === 1 ? '' : 's'} that ${plan.skipped === 1 ? "doesn't" : "don't"} apply to the selected element.`,
    );
  }
  if (plan.dropped > 0) {
    toast.info(
      `Dropped ${plan.dropped} keyframe${plan.dropped === 1 ? '' : 's'} that fall after the end of the scene.`,
    );
  }
}

/** Copies the selected keyframes to memory and, where allowed, the system clipboard. */
export function copySelection(store: Store): boolean {
  const { project, editor } = store.getState();
  const clip = buildClip(project, editor.selection.keyframeIds);
  if (!clip) return false;
  memoryClip = clip;
  if ('clipboard' in navigator) {
    navigator.clipboard.writeText(serialiseClip(clip)).catch(() => {
      // Clipboard access can be refused; the in-memory copy still works within this tab.
    });
  }
  return true;
}

/** Pastes clip data with its earliest keyframe at the playhead, as one undo step. */
export function pasteClip(store: Store, clip: ClipData, playhead: number): string[] {
  const { project, editor } = store.getState();
  const plan = planPaste(project, clip, editor.selection.elementIds, playhead);
  explain(plan);
  if (plan.placements.length === 0) return [];
  let created: string[] = [];
  store.updateProject('Paste keyframes', (draft) => {
    created = applyPlan(draft, plan);
  });
  store.updateEditor((draft) => {
    draft.selection.keyframeIds = created;
  });
  return created;
}

/** Pastes the in-memory copy, if there is one. Used by the context menu. */
export function pasteMemory(store: Store, playhead: number): boolean {
  if (!memoryClip) return false;
  pasteClip(store, memoryClip, playhead);
  return true;
}

/** Duplicates the selected keyframes so the earliest lands on the playhead. */
export function duplicateAtPlayhead(store: Store, playhead: number): string[] {
  const { project, editor } = store.getState();
  const clip = buildClip(project, editor.selection.keyframeIds);
  if (!clip) return [];
  const plan = planPaste(project, clip, [], playhead);
  explain(plan);
  if (plan.placements.length === 0) return [];
  let created: string[] = [];
  store.updateProject('Duplicate keyframes', (draft) => {
    created = applyPlan(draft, plan);
  });
  store.updateEditor((draft) => {
    draft.selection.keyframeIds = created;
  });
  return created;
}

/**
 * Listens for the browser's copy and paste events. Keyframe JSON on the clipboard is pasted as
 * keyframes; anything else is left for other handlers, such as SVG import.
 */
export function attachClipboard(
  store: Store,
  getPlayhead: () => number,
  isTextEntry: (t: EventTarget | null) => boolean,
): () => void {
  /** Puts keyframe JSON on the clipboard for the standard copy command. */
  const onCopy = (event: ClipboardEvent): void => {
    if (isTextEntry(event.target)) return;
    if (!copySelection(store) || !memoryClip) return;
    event.clipboardData?.setData('text/plain', serialiseClip(memoryClip));
    event.preventDefault();
  };
  /** Pastes keyframes from the clipboard, or from memory when the clipboard is empty. */
  const onPaste = (event: ClipboardEvent): void => {
    if (isTextEntry(event.target)) return;
    const text = event.clipboardData?.getData('text/plain') ?? '';
    const clip = parseClip(text) ?? (text.trim() === '' ? memoryClip : null);
    if (!clip) return;
    event.preventDefault();
    pasteClip(store, clip, getPlayhead());
  };
  document.addEventListener('copy', onCopy);
  document.addEventListener('paste', onPaste);
  return () => {
    document.removeEventListener('copy', onCopy);
    document.removeEventListener('paste', onPaste);
  };
}

/** Forgets the in-memory copy, for tests. */
export function clearMemoryClip(): void {
  memoryClip = null;
}
